import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import api from "../../../services/httpService";
import { setApiAccessToken } from "../../../localstorage";

export default function SignInCard() {
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();

  // Preserve the existing login route's session reset and API behavior.
  useEffect(() => { localStorage.clear(); }, []);
  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    if (showForgotPassword) {
      try {
        await api.post("/api/forgot-password", { userName: data.userName, email: data.email });
        setMessageSent(true);
      } catch (error) {
        toast.error("Unable to reset password");
      } finally { setLoading(false); }
      return;
    }
    try {
      const { data: response } = await api.post("/api/token/web/", { username: data.userName, password: data.password });
      setApiAccessToken(response.access);
      setLoading(false);
      navigate(response.first_time_login ? "/user-settings/password" : (response.access_policy?.landing_path || "/"));
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.code === "customer_reactivation_required") {
        navigate("/shared/account-inactive", { state: { reactivation: error.response.data } });
      } else {
        toast.error("Unable to login");
      }
      setLoading(false);
    }
  });
  return <aside className="signin-card" id="sign-in" aria-labelledby="signin-title">
    <div className="signin-topline"><span className="small-signal" /> YOUR WORKSPACE</div>
    {messageSent ? <>
      <h2 id="signin-title">Check your email</h2>
      <p>If an account with that email exists, we've sent instructions to reset your password.</p>
      <button className="forgot-button" type="button" onClick={() => { setMessageSent(false); setShowForgotPassword(false); }}>Back to Sign In</button>
    </> : <>
      <h2 id="signin-title">Welcome back.</h2>
      <p className="signin-subtitle">Sign in to LiveTakeoff</p>
      <form onSubmit={onSubmit} autoComplete="off">
        <label htmlFor="username">Username</label>
        <input id="username" type="text" autoComplete="username" placeholder="Enter username" {...register("userName", { required: "Username is required" })} aria-invalid={Boolean(errors.userName)} />
        {errors.userName && <p className="lt-form-error" role="alert">{errors.userName.message}</p>}
        {showForgotPassword ? <>
          <label htmlFor="reset-email">Email</label>
          <input id="reset-email" type="email" autoComplete="email" placeholder="Enter email" {...register("email", { required: "Email is required" })} aria-invalid={Boolean(errors.email)} />
          {errors.email && <p className="lt-form-error" role="alert">{errors.email.message}</p>}
        </> : <>
          <div className="password-label"><label htmlFor="password">Password</label><button type="button" className="forgot-button" onClick={() => setShowForgotPassword(true)}>Forgot your password?</button></div>
          <input id="password" type="password" autoComplete="current-password" placeholder="Enter password" {...register("password", { required: "Password is required" })} aria-invalid={Boolean(errors.password)} />
          {errors.password && <p className="lt-form-error" role="alert">{errors.password.message}</p>}
        </>}
        <button className="button button-red button-full" type="submit" disabled={loading}>{loading ? (showForgotPassword ? "sending..." : "signing in...") : (showForgotPassword ? "Reset password" : "Sign in")} <span aria-hidden="true">→</span></button>
        {showForgotPassword && <button type="button" className="forgot-button lt-signin-switch" onClick={() => setShowForgotPassword(false)}>Back to sign in</button>}
      </form>
      <p className="signin-footer">Existing customer and vendor accounts use this sign-in.</p>
      <p className="signin-footer">New customer? <Link to="/signup">Request a customer account</Link>.</p>
      <p className="signin-footer">New aircraft care provider? <Link to="/vendors/apply">Apply to join the network</Link>.</p>
    </>}
  </aside>;
}
